import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { Box, Typography, Stack, Link as MuiLink, Card, Divider, Checkbox, FormControlLabel, Alert, Container } from '@mui/material';
import { supabase } from '../lib/supabase';

export default function Auth() {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(location.state?.mode !== 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isLogin && !agreed) return;
    setIsLoading(true);

    try {
      if (isLogin) {
        const { error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (loginError) throw loginError;
      } else {
        const { error: signupError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/email-verification-success`,
            data: {
              full_name: name,
            }
          }
        });
        if (signupError) throw signupError;
        alert('Verification email sent! Please check your inbox.');
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'An error occurred during authentication');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
        {/* Top Logo - Centered */}
        <Box sx={{ pt: 6, display: 'flex', justifyContent: 'center' }}>
          <Stack direction="row" alignItems="center" spacing={1} component={Link} to="/" sx={{ textDecoration: 'none' }}>
            <Box sx={{ bgcolor: '#4f46e5', p: 0.5, borderRadius: 1.5, display: 'flex' }}>
              <Sparkles color="white" size={24} />
            </Box>
            <Typography variant="h5" fontWeight="bold" letterSpacing="-0.02em" sx={{ color: '#111827' }}>
              PostPilot <Box component="span" sx={{ color: '#4f46e5' }}>AI</Box>
            </Typography>
          </Stack>
        </Box>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%', maxWidth: 460 }}
          >
            <Card elevation={0} sx={{ p: { xs: 4, sm: 5 }, borderRadius: 4, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: '#111827', mb: 1.5, letterSpacing: '-0.02em' }}>
                  {isLogin ? 'Welcome back' : 'Create your account'}
                </Typography>
                <Typography variant="body1" color="#64748b" sx={{ mb: 2 }}>
                  {isLogin ? 'Login to your account to continue' : 'Start automating your social presence with AI'}
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {!isLogin && (
                <>
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    <Button variant="outline" size="md" sx={{ color: '#334155', borderColor: '#cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f8fafc' }, fontWeight: 500 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      Sign up with Google
                    </Button>
                    <Button variant="outline" size="md" sx={{ color: '#334155', borderColor: '#cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f8fafc' }, fontWeight: 500 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.627-5.373-12-12-12z" fill="#111827" />
                      </svg>
                      Sign up with GitHub
                    </Button>
                  </Stack>
                  <Divider sx={{ my: 3, '&::before, &::after': { borderColor: '#e2e8f0' } }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em' }}>
                      OR CONTINUE WITH EMAIL
                    </Typography>
                  </Divider>
                </>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {!isLogin && (
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#334155" sx={{ mb: 1 }}>Full Name</Typography>
                      <Input
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#334155" sx={{ mb: 1 }}>Email</Typography>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="#334155">Password</Typography>
                      {isLogin && (
                        <MuiLink component={Link} to="/auth/forgot-password" variant="caption" underline="hover" sx={{ color: '#4f46e5', fontWeight: 600 }}>
                          Forgot password?
                        </MuiLink>
                      )}
                    </Box>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    {!isLogin && (
                      <Typography variant="caption" color="#64748b" sx={{ mt: 0.5, display: 'block' }}>
                        Must be at least 8 characters
                      </Typography>
                    )}
                  </Box>

                  {!isLogin ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#4f46e5' } }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="#64748b">
                          By signing up, I agree to the <MuiLink href="#" color="#4f46e5" underline="hover">Terms of Service</MuiLink> and <MuiLink href="#" color="#4f46e5" underline="hover">Privacy Policy</MuiLink>.
                        </Typography>
                      }
                      sx={{ mt: 1, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { mt: 0.5 } }}
                    />
                  ) : (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#4f46e5' } }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="#475569" fontWeight={500}>
                          Remember me for 30 days
                        </Typography>
                      }
                      sx={{ mt: 0 }}
                    />
                  )}

                  <Button type="submit" size="lg" sx={{ width: '100%', mt: 1, bgcolor: '#4f46e5', color: 'white', '&:hover': { bgcolor: '#4338ca' }, py: 1.5, fontWeight: 'bold', fontSize: '1rem' }} isLoading={isLoading}>
                    {isLogin ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Login <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>&rarr;</span>
                      </Box>
                    ) : 'Create Account'}
                  </Button>
                </Stack>
              </form>

              {isLogin && (
                <>
                  <Divider sx={{ my: 3, '&::before, &::after': { borderColor: '#e2e8f0' } }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em' }}>
                      OR CONTINUE WITH
                    </Typography>
                  </Divider>
                  <Stack direction="row" spacing={2}>
                    <Button variant="outline" size="md" sx={{ flexGrow: 1, color: '#334155', borderColor: '#cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f8fafc' }, fontWeight: 500 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      Google
                    </Button>
                    <Button variant="outline" size="md" sx={{ flexGrow: 1, color: '#334155', borderColor: '#cbd5e1', bgcolor: 'white', '&:hover': { bgcolor: '#f8fafc' }, fontWeight: 500 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.627-5.373-12-12-12z" fill="#111827" />
                      </svg>
                      GitHub
                    </Button>
                  </Stack>
                </>
              )}
            </Card>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="#64748b">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <Typography
                  component="button"
                  onClick={() => setIsLogin(!isLogin)}
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: '#4f46e5', cursor: 'pointer', background: 'none', border: 'none', p: 0, ml: 1, '&:hover': { textDecoration: 'underline' } }}
                >
                  {isLogin ? 'Sign up' : 'Log in'}
                </Typography>
              </Typography>
            </Box>
          </motion.div>
        </Box>

        {/* Footer */}
        <Box component="footer" sx={{ py: 3, px: 4, borderTop: 1, borderColor: '#e2e8f0', bgcolor: '#f8f9fa' }}>
          <Container maxWidth="xl" disableGutters>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ bgcolor: '#e2e8f0', p: 0.5, borderRadius: 1, display: 'flex' }}>
                  <Sparkles color="#475569" size={16} />
                </Box>
                <Typography variant="body2" fontWeight="medium" color="#475569">
                  PostPilot AI © {new Date().getFullYear()}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={4}>
                <MuiLink href="#" variant="body2" color="#64748b" underline="hover">Privacy Policy</MuiLink>
                <MuiLink href="#" variant="body2" color="#64748b" underline="hover">Terms of Service</MuiLink>
                <MuiLink href="#" variant="body2" color="#64748b" underline="hover">Contact Support</MuiLink>
                <MuiLink href="#" variant="body2" color="#64748b" underline="hover">Documentation</MuiLink>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
